/**
 * @file orderRowSummary.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-29
 * @modified 2026-10-21
 * @description The Supplier/s and Item/s cells of a customer-order search row,
 *              built from the row's linked POs and its own lines. Lifted out of
 *              SearchCustomerOrders when Price Orders took on the same columns,
 *              so the two cannot summarise an order differently.
 *
 * 1.1.0 (2026-10-21, Cam — "in the main sales order rows use the item name"):
 * a PO line whose code the catalogue did not resolve takes the item name of
 * the order line it was bought for, before falling back to its own words.
 */

/**
 * An order's own lines that NO linked purchase order speaks for.
 *
 * A line reaches a purchase order or it does not: a stock line never will,
 * and one raised this morning has not yet. Those lines still carry a
 * supplier and a product, and a row showing a dash in both columns while the
 * order plainly has both is simply wrong. Where a line IS on a linked PO the
 * PO's version wins — it is what was actually ordered from the mill, and the
 * two can differ.
 */
export function uncoveredLinesOf(order) {
  const linkedIds = new Set((order.linked_pos || []).map(po => po.id));
  return (order.order_lines || []).filter(
    line => !(line.purchase_order_id && linkedIds.has(line.purchase_order_id)));
}

/**
 * The suppliers behind an order, one entry per supplier rather than per
 * purchase order: two POs to the same mill is one supplier on the row.
 * Keyed on the account id where there is one, and on the name otherwise.
 *
 * Names only — no account badge. A supplier's account number is legitimately
 * null on many accounts, and the column has to fit several of them.
 */
export function suppliersOf(order) {
  const seen = new Map();
  const add = (id, name) => {
    if (!name) return;
    const key = id ?? `name:${name}`;
    if (!seen.has(key)) seen.set(key, { id: id ?? null, name });
  };
  for (const po of (order.linked_pos || [])) add(po.supplier_id, po.supplier_name);
  for (const line of uncoveredLinesOf(order)) add(line.supplier_id, line.supplier_name);
  return [...seen.values()];
}

/**
 * What was ordered. Quantities for the same code are summed rather than
 * listed twice: an order split across two POs for the same product has still
 * ordered one total amount of it, and that is the number someone scanning
 * the column wants.
 *
 * Lines with no code are grouped under their own wording — a free-text line
 * is still something that was ordered. A PO's haulage line is not something
 * that was ordered, and is left out.
 */
export function itemsOf(order) {
  const seen = new Map();
  const add = (code, name, quantity) => {
    const trimmed = (code || '').trim();
    const key = trimmed.toLowerCase() || `desc:${(name || '').trim().toLowerCase()}`;
    if (key === 'desc:') return;
    const existing = seen.get(key);
    if (existing) existing.quantity += Number(quantity) || 0;
    else seen.set(key, { key, code: trimmed, name: name || '', quantity: Number(quantity) || 0 });
  };
  // The item's name: the catalogue's for the PO line's code, else the item
  // name of the order line it was bought for, and only then the line's own
  // wording (a typed line with no item). Never the name over the wording
  // anywhere else — this column names WHAT was ordered.
  const orderLineName = new Map((order.order_lines || [])
    .filter(l => l && l.id != null && l.item_name)
    .map(l => [Number(l.id), l.item_name]));
  for (const po of (order.linked_pos || [])) {
    for (const line of (po.lines || [])) {
      if (line.kind === 'haulage') continue;
      const fromOrder = line.order_line_id != null ? orderLineName.get(Number(line.order_line_id)) : null;
      add(line.item_code, line.item_name || fromOrder || line.description, line.quantity);
    }
  }
  // product_name is the order screen's editable Description cell, so the
  // resolved item name leads here exactly as the PO line's does.
  for (const line of uncoveredLinesOf(order)) {
    add(line.product_code, line.item_name || line.product_name, line.quantity);
  }
  return [...seen.values()];
}
